import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "./DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CountUp } from "@/components/landing/Motion";
import { StaticWaveform } from "@/components/landing/Waveform";
import { Plus, Play, FileAudio, BarChart3, Users, Wallet, ArrowUpRight } from "lucide-react";

const KPI = ({ label, value, suffix = "", trend, color = "#FF5B3E", icon: Icon }) => (
    <div className="bg-white rounded-3xl p-6 border border-cream-300 shadow-soft">
        <div className="flex items-center justify-between mb-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{background: color + "22", color}}>
                <Icon size={16}/>
            </div>
            {trend && <span className="text-[11px] font-mono font-bold text-mint bg-mint/15 px-2 py-0.5 rounded-full">{trend}</span>}
        </div>
        <p className="text-[12px] uppercase tracking-[0.18em] font-semibold text-ink-muted">{label}</p>
        <p className="font-display text-[36px] sm:text-[42px] font-bold leading-none mt-2">
            <CountUp to={value} suffix={suffix}/>
        </p>
    </div>
);

export default function Overview() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [articles, setArticles] = useState([]);
    const [usage, setUsage] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [a, b, c] = await Promise.all([
                    api.get("/analytics/overview"),
                    api.get("/articles"),
                    api.get("/billing/usage"),
                ]);
                setData(a.data);
                setArticles(b.data);
                setUsage(c.data);
            } catch {}
        })();
    }, []);

    const weekPlays = data ? (data.plays_by_day || []).slice(-7).map(d => d.plays) : [];
    const max = weekPlays.length ? Math.max(...weekPlays, 1) : 1;
    const monthlyTotal = usage?.monthly_chars_total ?? 0;
    const monthlyRemaining = usage?.monthly_chars_remaining ?? 0;
    const topupRemaining = usage?.topup_chars_remaining ?? 0;

    return (
        <div data-testid="dashboard-overview">
            <PageHeader
                eyebrow="Overview"
                title={<>Hello, <span className="italic-display text-coral">{user?.name?.split(" ")[0] || "Editor"}</span>.</>}
                subtitle="Here's what your audio is doing today."
                action={
                    <Link to="/dashboard/articles/new" data-testid="quick-new-article" className="bg-ink hover:bg-coral text-cream-100 rounded-full px-5 py-3 text-[14px] font-semibold inline-flex items-center gap-2 transition-colors whitespace-nowrap">
                        <Plus size={14}/> New article
                    </Link>
                }
            />

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
                <KPI label="Total plays" value={data?.total_plays || 0} color="#FF5B3E" icon={Play}/>
                <KPI label="Articles" value={data?.total_articles || 0} color="#3D5BFF" icon={FileAudio}/>
                <KPI label="Avg completion" value={data?.avg_completion_pct || 0} suffix="%" color="#2EE8B5" icon={BarChart3}/>
                <KPI label="Listening now" value={data?.active_listeners_today || 0} color="#FFD23F" icon={Users}/>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-6">
                {/* Weekly plays chart */}
                <div className="lg:col-span-2 bg-white rounded-3xl p-6 lg:p-7 border border-cream-300 shadow-soft">
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted">This week</p>
                            <h3 className="font-display text-[24px] font-semibold tracking-tight text-ink">Plays per day</h3>
                        </div>
                        <span className="text-[13px] font-mono text-coral font-bold">{weekPlays.reduce((a, b) => a + b, 0)} plays</span>
                    </div>
                    {data && (
                        <div className="h-48 flex items-end gap-3">
                            {weekPlays.map((v, i) => (
                                <div key={i} className="flex-1 flex flex-col items-center gap-2">
                                    <div className="w-full rounded-t-xl transition-all hover:opacity-80" style={{height: `${Math.max(4, (v / max) * 100)}%`, background: i === weekPlays.length - 1 ? "#FF5B3E" : "#0A0A0A"}}/>
                                    <span className="text-[10px] font-mono text-ink-muted">{["M","T","W","T","F","S","S"][i % 7]}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Plan + quick links */}
                <div className="space-y-5">
                    <div className="bg-coral text-white rounded-3xl p-6 relative overflow-hidden">
                        <Wallet size={18} className="mb-4 opacity-80"/>
                        <p className="text-[11px] uppercase tracking-[0.22em] font-semibold opacity-80">Character limit</p>
                        <p className="font-display text-[36px] font-bold mt-1 leading-none">
                            {monthlyRemaining.toLocaleString()} <span className="text-[18px] opacity-70">/ {monthlyTotal.toLocaleString()}</span>
                        </p>
                        <p className="text-[12px] opacity-90 mt-2">characters remaining this cycle</p>
                        <p className="text-[11px] opacity-90 mt-1">Top-up remaining: {topupRemaining.toLocaleString()} chars</p>
                        <Link to="/dashboard/billing" className="inline-flex items-center gap-1 text-[12.5px] font-semibold mt-4 underline-offset-4 hover:underline">
                            Manage <ArrowUpRight size={12}/>
                        </Link>
                    </div>
                    <Link to="/dashboard/embed" className="block bg-ink-pill text-cream-100 rounded-3xl p-6 hover:bg-black transition-colors">
                        <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-coral">Quick</p>
                        <p className="font-display text-[20px] font-semibold tracking-tight mt-1">Get embed code →</p>
                    </Link>
                </div>
            </div>

            {/* Recent articles */}
            <div className="mt-6 bg-white rounded-3xl border border-cream-300 shadow-soft overflow-hidden">
                <div className="flex items-center justify-between p-6 border-b border-cream-300">
                    <h3 className="font-display text-[22px] font-semibold tracking-tight">Recent articles</h3>
                    <Link to="/dashboard/articles" className="text-[13px] text-coral hover:underline font-semibold">View all →</Link>
                </div>
                {articles.length === 0 ? (
                    <div className="p-10 text-center">
                        <p className="text-[15px] text-ink-muted mb-4">No articles yet. Generate your first audio.</p>
                        <Link to="/dashboard/articles/new" className="inline-flex items-center gap-2 bg-ink hover:bg-coral text-cream-100 rounded-full px-5 py-3 text-[14px] font-semibold transition-colors">
                            <Plus size={14}/> Create article
                        </Link>
                    </div>
                ) : (
                    <ul className="divide-y divide-cream-300">
                        {articles.slice(0, 5).map(a => (
                            <li key={a.id} data-testid={`overview-article-${a.id}`} className="flex items-center gap-4 p-5 hover:bg-cream-100 transition-colors">
                                <button className="w-10 h-10 rounded-full bg-coral text-white flex items-center justify-center flex-shrink-0">
                                    <Play size={12} className="ml-0.5" fill="currentColor"/>
                                </button>
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-ink text-[14.5px] truncate">{a.title}</p>
                                    <p className="text-[12px] text-ink-muted">{a.voice_id} · {Math.floor(a.duration_sec / 60)}:{(a.duration_sec % 60).toString().padStart(2, "0")} · {a.plays} plays</p>
                                </div>
                                <div className="hidden sm:block w-32 h-8">
                                    <StaticWaveform bars={20} color="#0A0A0A" height={28}/>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
